import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { noteService } from '../services/noteService';
import { PiFolderPlus, PiPlusCircle, PiTrash, PiPencil, PiCheck, PiEye, PiPencilLine, PiDotsThreeVertical, PiTrashSimple, PiPencilSimpleLine, PiDotsThreeOutline } from 'react-icons/pi';
import MDEditor from '@uiw/react-md-editor';
import { Menu } from '@headlessui/react';

function Notes() {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [renamingFolder, setRenamingFolder] = useState(null);

  useEffect(() => {
    loadFolders();
  }, []);

  const loadFolders = async () => {
    try {
      setLoading(true);
      const response = await noteService.getFolders();
      setFolders(response.data);
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    try {
      await noteService.createFolder({ name: newFolderName });
      setShowFolderDialog(false);
      setNewFolderName('');
      loadFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleSelectFolder = async (folder) => {
    setSelectedFolder(folder);
    setSelectedNote(null);
    try {
      const response = await noteService.getNotes(folder._id);
      const updatedFolder = { ...folder, notes: response.data };
      setFolders(folders.map(f => f._id === folder._id ? updatedFolder : f));
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const handleCreateNote = async (folderId) => {
    try {
      const newNote = await noteService.createNote(folderId, {
        title: 'New Note',
        content: '# New Note\nStart writing here...'
      });
      const updatedNotes = await noteService.getNotes(folderId);
      setFolders(folders.map(f => 
        f._id === folderId ? { ...f, notes: updatedNotes.data } : f
      ));
      setSelectedNote(newNote.data);
      setIsEditing(true);
      setEditedContent(newNote.data.content);
      setEditedTitle(newNote.data.title);
    } catch (error) {
      console.error('Error creating note:', error);
    }
  };

  const handleSaveNote = async () => {
    try {
      const response = await noteService.updateNote(selectedFolder._id, selectedNote._id, {
        title: editedTitle,
        content: editedContent
      });
      
      // Update the local state with the saved note
      const updatedNote = response.data;
      setSelectedNote(updatedNote);
      setIsEditing(false);
      
      // Update the folders state to reflect the changes
      const updatedFolders = folders.map(folder => {
        if (folder._id === selectedFolder._id) {
          const updatedNotes = folder.notes.map(note => 
            note._id === updatedNote._id ? updatedNote : note
          );
          return { ...folder, notes: updatedNotes };
        }
        return folder;
      });
      
      setFolders(updatedFolders);
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (window.confirm('Are you sure you want to delete this folder and all its notes?')) {
      try {
        await noteService.deleteFolder(folderId);
        setFolders(folders.filter(f => f._id !== folderId));
        if (selectedFolder?._id === folderId) {
          setSelectedFolder(null);
          setSelectedNote(null);
        }
      } catch (error) {
        console.error('Error deleting folder:', error);
      }
    }
  };

  const handleRenameFolder = async (folderId, newName) => {
    try {
      await noteService.updateFolder(folderId, { name: newName });
      setFolders(folders.map(f => 
        f._id === folderId ? { ...f, name: newName } : f
      ));
      setRenamingFolder(null);
    } catch (error) {
      console.error('Error renaming folder:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    
    try {
      await noteService.deleteNote(selectedFolder._id, noteId);
      
      // Update the folders state to remove the deleted note
      const updatedFolders = folders.map(folder => {
        if (folder._id === selectedFolder._id) {
          const updatedNotes = folder.notes.filter(note => note._id !== noteId);
          return { ...folder, notes: updatedNotes };
        }
        return folder;
      });
      
      setFolders(updatedFolders);
      
      // If the deleted note was selected, clear the selection
      if (selectedNote?._id === noteId) {
        setSelectedNote(null);
        setEditedContent('');
        setEditedTitle('');
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Left Panel - Folders and Notes List */}
      <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col md:w-72 lg:w-80">
        {/* Folders Header */}
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => setShowFolderDialog(true)}
            className="w-10 h-10 flex items-center justify-center bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors mx-auto"
          >
            <PiFolderPlus className="w-5 h-5" />
          </button>
        </div>

        {/* Folders and Notes List with Loading State */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            Array(5).fill().map((_, index) => (
              <div key={index} className="p-3 border-b border-gray-200">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3"></div>
              </div>
            ))
          ) : (
            folders.map((folder) => (
              <div key={folder._id} className="border-b border-gray-200">
                <div className="flex items-center justify-between p-3 hover:bg-gray-100">
                  {renamingFolder === folder._id ? (
                    <input
                      type="text"
                      defaultValue={folder.name}
                      onBlur={(e) => handleRenameFolder(folder._id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameFolder(folder._id, e.target.value);
                        }
                      }}
                      autoFocus
                      className="flex-1 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span 
                      className="font-medium cursor-pointer flex-1"
                      onClick={() => handleSelectFolder(folder)}
                    >
                      {folder.name}
                    </span>
                  )}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateNote(folder._id);
                      }}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <PiPlusCircle className="w-4 h-4" />
                    </button>
                    <Menu as="div" className="relative">
                      <Menu.Button className="p-1 hover:bg-gray-200 rounded">
                        <PiDotsThreeVertical className="w-4 h-4" />
                      </Menu.Button>
                      <Menu.Items className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } flex items-center px-4 py-2 text-sm w-full text-gray-700`}
                              onClick={() => setRenamingFolder(folder._id)}
                            >
                              <PiPencilSimpleLine className="w-4 h-4 mr-2" />
                              Rename
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? 'bg-red-50' : ''
                              } flex items-center px-4 py-2 text-sm w-full text-red-600`}
                              onClick={() => handleDeleteFolder(folder._id)}
                            >
                              <PiTrashSimple className="w-4 h-4 mr-2" />
                              Delete
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Menu>
                  </div>
                </div>
                {/* Notes List */}
                {selectedFolder?._id === folder._id && folder.notes && (
                  <div className="pl-4">
                    {folder.notes.map((note) => (
                      <div
                        key={note._id}
                        className="group flex items-center justify-between p-2 cursor-pointer hover:bg-gray-100"
                      >
                        <span 
                          className="text-sm truncate flex-1"
                          onClick={() => {
                            setSelectedNote(note);
                            setEditedContent(note.content);
                            setEditedTitle(note.title);
                            setIsEditing(false);
                          }}
                        >
                          {note.title}
                        </span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedNote(note);
                              setEditedContent(note.content);
                              setEditedTitle(note.title);
                              setIsEditing(true);
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <PiPencilLine className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNote(note._id);
                            }}
                            className="p-1 hover:bg-red-100 rounded"
                          >
                            <PiTrashSimple className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Note Editor */}
      <div className="flex-1 flex flex-col bg-white relative">
        {selectedNote ? (
          <>
            <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-white sticky top-0 z-10">
              {isEditing ? (
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-xl font-medium w-full outline-none"
                />
              ) : (
                <h2 className="text-xl font-medium">{selectedNote.title}</h2>
              )}
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleSaveNote}
                      className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-1"
                    >
                      <PiCheck className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-1"
                    >
                      <PiEye className="w-4 h-4" />
                      Preview
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1"
                  >
                    <PiPencilLine className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              {isEditing ? (
                <div className="h-full">
                  <MDEditor
                    value={editedContent}
                    onChange={setEditedContent}
                    height="100%"
                    preview="edit"
                    className="wmde-markdown-var h-full"
                    previewOptions={{
                      style: {
                        backgroundColor: 'white',
                        color: '#374151'
                      }
                    }}
                    textareaProps={{
                      style: {
                        color: '#374151',
                        backgroundColor: 'white'
                      }
                    }}
                    toolbarHeight={50}
                    visibleDragbar={false}
                    hideToolbar={false}
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem'
                    }}
                  />
                </div>
              ) : (
                <div className="prose max-w-none p-8 h-full overflow-auto">
                  <MDEditor.Markdown
                    source={selectedNote.content}
                    style={{
                      backgroundColor: 'white',
                      color: '#374151',
                      fontSize: '1rem',
                      lineHeight: '1.75'
                    }}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-2">Select a note to view or edit</p>
              <p className="text-sm">Or create a new note using the + button</p>
            </div>
          </div>
        )}
      </div>

      {/* New Folder Dialog */}
      {showFolderDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowFolderDialog(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notes;
